/**
 * Dock Manager
 * Handles Mobile Dock interactions, modal exclusivity, and smart scroll behavior.
 */
class DockManager {
  constructor() {
    this.openModal = null;
    this.dockEl = document.querySelector('.mobile-dock');
    this.init();
    this.initStickyObserver();
    this.initScrollCollapse();
    this.initDesktopChromeClose();
    this.initDockOffset();
  }

  /**
   * Publishes --dock-offset: the space fixed bottom UI (mobile dock, PDP add-to-cart)
   * occupies, so other sticky bars can sit above it instead of underneath.
   */
  initDockOffset() {
    const update = () => this.updateDockOffset();

    window.addEventListener('resize', update, { passive: true });

    const watched = [
      this.dockEl,
      ...document.querySelectorAll('.mobile-dock__product-actions, .header__context-actions--product'),
    ].filter(Boolean);

    const mutationObserver = new MutationObserver(update);
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;

    for (const element of watched) {
      mutationObserver.observe(element, { attributes: true, attributeFilter: ['class'] });
      resizeObserver?.observe(element);
    }

    update();
  }

  updateDockOffset() {
    const isDesktop = window.matchMedia('(width >= 75em)').matches;
    const isShown = (element) =>
      Boolean(element) &&
      (element.classList.contains('dock-visible') || element.classList.contains('dock-added-confirmation-active'));
    let offset = 0;

    if (isDesktop) {
      const cta = document.querySelector('.header__context-actions--product');

      if (isShown(cta)) {
        offset = (Number.parseFloat(getComputedStyle(cta).bottom) || 0) + cta.offsetHeight;
      }
    } else if (this.dockEl) {
      const dockStyle = getComputedStyle(this.dockEl);
      const mainBar = this.dockEl.querySelector('.mobile-dock__main-bar');
      const actions = this.dockEl.querySelector('.mobile-dock__product-actions');

      offset = (Number.parseFloat(dockStyle.bottom) || 0) + (mainBar?.offsetHeight ?? 0);

      if (isShown(actions)) {
        offset += (Number.parseFloat(dockStyle.rowGap) || 0) + actions.offsetHeight;
      }
    }

    document.documentElement.style.setProperty('--dock-offset', `${offset}px`);
  }

  /**
   * Sheets live below 75em (same as .header__desktop-menu). Close them when
   * crossing into desktop chrome so body.dock-open cannot trap the page.
   */
  initDesktopChromeClose() {
    const desktopChromeMq = window.matchMedia('(width >= 75em)');

    const onChange = (event) => {
      if (event.matches) {
        this.closeAll();
      }
    };

    if (desktopChromeMq.addEventListener) {
      desktopChromeMq.addEventListener('change', onChange);
    } else {
      desktopChromeMq.addListener(onChange);
    }
  }

  /**
   * Shrink + soften the floating dock while the page is scrolling, restore when idle.
   */
  initScrollCollapse() {
    const dock = this.dockEl;

    if (!dock) {
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    let ticking = false;
    let idleTimer = null;
    const idleMs = 180;

    const setCompact = (compact) => {
      if (document.body.classList.contains('dock-open')) {
        dock.classList.remove('mobile-dock--compact');
        return;
      }

      dock.classList.toggle('mobile-dock--compact', compact);
    };

    const onScroll = () => {
      if (ticking) {
        return;
      }

      ticking = true;

      requestAnimationFrame(() => {
        setCompact(true);
        ticking = false;

        clearTimeout(idleTimer);
        idleTimer = setTimeout(() => setCompact(false), idleMs);
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  init() {
    // 1. Listen for 'toggle' on details to manage the .active class (Unified Transition)
    document.addEventListener(
      'toggle',
      (event) => {
        if (event.target.tagName === 'DETAILS') {
          const modal = event.target.querySelector('.dock-modal');
          if (modal) {
            if (event.target.open) {
              this.registerOpen(event.target);
            } else {
              // Handle closing (e.g. via X button)
              if (this.openModal === event.target) {
                this.openModal = null;
                document.body.classList.remove('dock-open');
              }
            }
          }
        }
      },
      { capture: true },
    );

    // 2. Click Outside / Backdrop Click Listener
    document.addEventListener('click', (event) => {
      if (!this.openModal) return;

      const target = event.target;

      // Define what counts as a "Backdrop" for our various modals
      const isBackdropClick =
        target.classList.contains('mobile-facets') || // Filters Backdrop
        target.classList.contains('search-modal') || // Search Backdrop (if applicable)
        target.classList.contains('menu-drawer-container') || // Menu Backdrop
        target.classList.contains('cart-drawer'); // Cart Backdrop

      // Also check if we clicked *inside* the actual content (the white part)
      const isContentClick = target.closest('.dock-modal');

      // If we clicked the backdrop OR we clicked somewhere that isn't the content (and isn't the opener)
      if (isBackdropClick || (!isContentClick && !this.isTrigger(target))) {
        // Prevent the same click activating links/buttons under the visual backdrop
        // (e.g. collection product-card stretched links) once the dock closes.
        event.preventDefault();
        event.stopPropagation();
        this.closeAll();
      }
    });
  }

  isTrigger(target) {
    return (
      target.closest('summary') ||
      target.closest('.header__icon') ||
      target.closest('.header__context-btn') ||
      target.closest('.mobile-dock__context-btn') ||
      target.closest('.mobile-dock__item') ||
      target.closest('.js-size-guide-trigger') ||
      target.closest('.js-dock-modal-trigger') ||
      target.closest('.js-pers-edit') ||
      target.closest('.js-pers-save') ||
      target.closest('.js-ps-edit') ||
      target.closest('.js-ps-save') ||
      target.closest('.photo-stitch-lightbox') ||
      target.closest('.js-ps-lightbox-close')
    ); /* Key Fix: Allow dock buttons! */
  }

  initSwipeListeners(modal) {
    if (modal.dataset.swipeInitialized) return;

    const handle = modal.querySelector('.dock-modal__drag-handle');
    if (!handle) return;

    let startY = 0;
    let currentY = 0;
    let isDragging = false;

    // Touch Start
    handle.addEventListener(
      'touchstart',
      (e) => {
        startY = e.touches[0].clientY;
        isDragging = true;
        modal.style.transition = 'none'; // Disable transition for direct 1:1 movement
        // e.preventDefault(); // Don't prevent default here to allow click? No, drag handle shouldn't click.
        e.stopPropagation(); // Stop bubbling to document click
      },
      { passive: false },
    ); // key for e.preventDefault() if needed, but we start with false

    // Touch Move
    handle.addEventListener(
      'touchmove',
      (e) => {
        if (!isDragging) return;

        // Prevent scrolling the body/modal while dragging handle
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();

        currentY = e.touches[0].clientY;
        const deltaY = currentY - startY;

        // Only allow dragging down (positive delta)
        if (deltaY > 0) {
          // Add linear resistance
          modal.style.transform = `translateY(${deltaY}px)`;
        }
      },
      { passive: false },
    );

    // Touch End
    handle.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      isDragging = false;
      e.stopPropagation();

      const deltaY = currentY - startY;
      const threshold = 100; // Lower threshold slightly for better feel

      // Remove inline style to let CSS class/transition take over
      modal.style.transition = '';

      if (deltaY > threshold) {
        // Dragged far enough: Close it
        this.closeAll();
        // Clear inline transform immediately. While it is set, it overrides the closed
        // state (.dock-modal translateY(100%)), so the sheet would freeze until removed
        // and the CSS transition could not run from the drag position to off-screen.
        modal.style.transform = '';
      } else {
        // Snap back

        requestAnimationFrame(() => {
          modal.style.transform = ''; // Reverts to class state (translateY(0))
        });
      }
      // Reset trackers
      startY = 0;
      currentY = 0;
    });

    modal.dataset.swipeInitialized = 'true';
  }

  // Called when a modal intentionally opens
  open(element) {
    this.closeAll(); // Close others first
    // We don't need to add classes or semantic state here,
    // the component's own open logic (setAttribute open, etc) handles visibility.
    this.registerOpen(element);
  }

  // Register state
  registerOpen(element) {
    // If we are opening something new, make sure everything else is closed first
    if (this.openModal && this.openModal !== element) {
      this.closeAll(element);
    }
    this.openModal = element;
    document.body.classList.add('dock-open'); // Trigger CSS effects

    if (this.dockEl) {
      this.dockEl.classList.remove('mobile-dock--compact');
    }

    // Add active class (for CSS transitions)
    element.classList.add('active');

    if (element.id === 'SearchModal') {
      const searchInput = element.querySelector('#Search-In-Modal');
      if (searchInput) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => searchInput.focus());
        });
      }
    }

    // Initialize Swipe Listeners if needed
    const modal = element.classList.contains('dock-modal') ? element : element.querySelector('.dock-modal');
    if (modal) {
      // FORCE RESET TRANSFORM: Critical fix for "opens 1/3 height" bug
      modal.style.transform = '';
      this.initSwipeListeners(modal);
    }
  }

  // Close everything EXCEPT the current opener (if provided)
  closeAll(exceptElement = null) {
    // 1. Close Class-based (Search, Menu, Cart, Filters)
    for (const modal of document.querySelectorAll('.dock-modal.active, .menu-drawer.active, .search-modal.active')) {
      if (modal === exceptElement) continue;

      // Remove active class
      modal.classList.remove('active');

      if (modal.id === 'MenuDrawer' && typeof modal.resetNavigation === 'function') {
        modal.resetNavigation();
      }

      // Clean up body class if nothing is open
      if (this.openModal === modal) {
        this.openModal = null;
      }

      // Close Custom Elements (CartDrawer) if they have a close method
      const customElement = modal.closest('cart-drawer');
      if (customElement && typeof customElement.close === 'function') {
        customElement.close();
      }

      // Cleanup Mobile Facets Wrapper
      const facetsWrapper = modal.closest('.mobile-facets__wrapper');
      if (facetsWrapper) facetsWrapper.classList.remove('menu-open');
    }

    // 2. Legacy Details (if any remain)
    for (const details of document.querySelectorAll('details:has(.dock-modal)[open]')) {
      if (details === exceptElement) continue;
      details.removeAttribute('open');
      const summary = details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    }

    if (!exceptElement) {
      this.openModal = null;
      document.body.classList.remove('dock-open'); // Reset CSS effects
    }
  }
  // Observer: Show Dock Button ONLY when Product Form is NOT visible
  initStickyObserver() {
    // Cleanup old observer if it exists
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    const productActionTargets = document.querySelectorAll('.mobile-dock__product-actions, .header__context-actions--product');
    if (productActionTargets.length === 0) return;

    // Watch the product info area instead of just the form
    const productInfo = document.querySelector('.product__info-container');

    if (productInfo) {
      let dockProductActionsVisible = null;

      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const shouldShow = !entry.isIntersecting;

            if (dockProductActionsVisible === shouldShow) {
              continue;
            }

            dockProductActionsVisible = shouldShow;

            for (const target of productActionTargets) {
              target.classList.toggle('dock-visible', shouldShow);
            }
          }
        },
        {
          root: null,
          threshold: 0,
          rootMargin: '0px 0px -8px 0px',
        },
      );

      this.observer.observe(productInfo);
    } else {
      // Fallback for pages without the form
      for (const target of productActionTargets) {
        target.classList.add('dock-visible');
      }
    }
  }

  // Helper: Toggle Details Element (Search/Menu)
  toggleDetails(details) {
    if (!details) return;

    if (details.hasAttribute('open')) {
      this.closeAll();
    } else {
      this.closeAll(); // Close others first
      details.setAttribute('open', 'true');
      const summary = details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'true');

      // Robust Focus Handling
      const input = details.querySelector('input:not([type="hidden"]), a, button');
      if (input) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => input.focus());
        });
      }
    }
  }
}

window.dockManager = new DockManager();

document.addEventListener('DOMContentLoaded', () => {
  // 1. Search Trigger (Classes)
  const searchTriggers = [document.getElementById('DockSearchTrigger'), document.getElementById('SearchTrigger')];
  for (const trigger of searchTriggers) {
    if (trigger) {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const searchModal = document.getElementById('SearchModal');
        if (searchModal) window.dockManager.open(searchModal);
      });
    }
  }

  // 2. Menu Trigger (Classes)
  const menuTriggers = [document.getElementById('DockMenuTrigger'), document.getElementById('MenuTrigger')];
  for (const trigger of menuTriggers) {
    if (trigger) {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const menuDrawer = document.getElementById('MenuDrawer');
        if (menuDrawer) window.dockManager.open(menuDrawer);
      });
    }
  }

  // 3. Cart Trigger (Delegation)
  const cartTrigger = document.getElementById('DockCartTrigger');
  if (cartTrigger) {
    cartTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      window.dockManager.closeAll();

      const originalCartIcon = document.getElementById('cart-icon-bubble');
      if (originalCartIcon) {
        originalCartIcon.click();

        // Explicitly tell DockManager we opened the cart
        const cartDockModal = document.querySelector('.cart-drawer.dock-modal');
        if (cartDockModal) {
          setTimeout(() => window.dockManager.registerOpen(cartDockModal), 50);

          // FIX: Listen for Native Close Events to cleanup DockManager state
          const closeBtn = cartDockModal.querySelector('.cart-drawer__close');
          if (closeBtn) {
            closeBtn.onclick = () => window.dockManager.closeAll();
          }
        }
      }
    });
  }

  // Global Key Listener for "Escape" to sync DockManager
  document.addEventListener('keyup', (event) => {
    if (event.code !== 'Escape') return;
    if (document.querySelector('.photo-stitch-lightbox[open]')) return;

    window.dockManager.closeAll();
  });

  // 4. Collection Filter Trigger (Event Delegation for AJAX safety)
  document.addEventListener('click', (e) => {
    const filterTrigger =
      e.target.closest('#FilterTrigger') ||
      e.target.closest('#DockFilterTrigger') ||
      e.target.closest('.js-filter-trigger');
    if (filterTrigger) {
      e.preventDefault();
      e.stopPropagation();

      // Logic via header/drawer interactions
      // We need to support BOTH specific Dock logic and the legacy/native logic if needed.
      // But here we want to open the #MobileFacetsWrapper

      const filterDrawer = document.getElementById('MobileFacetsWrapper');
      if (filterDrawer) {
        if (filterDrawer.classList.contains('active')) {
          window.dockManager.closeAll();
        } else {
          window.dockManager.open(filterDrawer);
          // Also toggle the parent wrapper class for CSS visibility (replacing details[open])
          const wrapper = filterDrawer.closest('.mobile-facets__wrapper');
          if (wrapper) wrapper.classList.add('menu-open');
        }
      } else {
        // Fallback for native details if we reverted (unlikely but safe)
        const nativeDrawer = document.querySelector('menu-drawer details.mobile-facets__disclosure');
        if (nativeDrawer) window.dockManager.toggleDetails(nativeDrawer);
      }
    }
  });

  // 5. Product context action: add to cart when ready, otherwise scroll to options
  document.addEventListener('click', (e) => {
    const productTrigger = e.target.closest('.js-product-trigger');

    if (!productTrigger) {
      return;
    }

    const productForm = document.querySelector('product-form');
    const form = productForm?.querySelector('form');
    const variantPicker = document.querySelector('variant-picker');
    const submitBtn = form?.querySelector('.product-form__submit, button[type="submit"][name="add"]');
    const idInput = form?.querySelector('[name="id"]');
    const canAddToCart =
      submitBtn &&
      !submitBtn.disabled &&
      submitBtn.getAttribute('aria-disabled') !== 'true' &&
      idInput &&
      idInput.value &&
      !idInput.disabled;

    if (canAddToCart) {
      if (productForm && typeof window.setFlyToCartOrigin === 'function') {
        window.setFlyToCartOrigin(productForm, productTrigger);
      }

      submitBtn.click();
      return;
    }

    if (variantPicker) {
      variantPicker.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (submitBtn) {
      submitBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });

  // 6. Size Guide Modal Trigger
  document.addEventListener('click', (e) => {
    const sizeGuideTrigger = e.target.closest('.js-size-guide-trigger');
    if (sizeGuideTrigger) {
      e.preventDefault();
      e.stopPropagation();
      sessionStorage.setItem('sizeGuideOpened', '1');

      const targetId = sizeGuideTrigger.getAttribute('data-target');
      const sizeGuideModal = document.getElementById(targetId);

      if (sizeGuideModal) {
        window.dockManager.open(sizeGuideModal);
      } else {
        console.error('[DockManager] Could not find modal with ID:', targetId);
      }
    }
  });

  // 7. Generic Dock Modal Trigger (any element pointing at a dock modal via data-target)
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('.js-dock-modal-trigger');

    if (!trigger) {
      return;
    }

    e.preventDefault();

    const targetId = trigger.getAttribute('data-target');
    const modal = document.getElementById(targetId);

    if (modal) {
      window.dockManager.open(modal);
    } else {
      console.error('[DockManager] Could not find modal with ID:', targetId);
    }
  });
});
